import { stateCLEARSTORAGE } from '@/store/stateCLEARSTORAGE.ts'
import { stateREHYDRATE } from '@/store/stateREHYDRATE.ts'
import {
	type Draft,
	type EnhancedStore,
	type EntityAdapter,
	type EntityId,
	type EntityState,
	type Middleware,
	type Reducer,
	type ReducersMapObject,
	type StoreEnhancer,
	type ThunkDispatch,
	Tuple,
	type UnknownAction,
	configureStore,
} from '@reduxjs/toolkit'
import { enableMapSet } from 'immer'
import { DateTime } from 'luxon'
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'

import type { IAPIResult } from './core/api/APIResult.ts'
import type { DefaultRootState } from './coreReducer'

enableMapSet()

export const createClass = <P extends new (...args: any[]) => object>(
	prototype: P,
): new (...args: any[]) => InstanceType<P> =>
	class extends prototype {} as unknown as new (...args: any[]) => InstanceType<P>

export const rehydrateAdapters = <Entity>(
	adapter: EntityAdapter<Entity, EntityId>,
	entityState: EntityState<Entity, EntityId>,
	entities: Record<EntityId, Entity>,
	Instance: new (...args: any[]) => Entity,
) => {
	adapter.setAll(
		entityState,
		Object.entries<Entity>(entities as unknown as Entity[]).map(([, value]) => new Instance(value)),
	)
}

export const fulfillDeploymentAPIResult = <
	Entity,
	K extends keyof DefaultRootState,
	S extends Draft<DefaultRootState[K]> & { lastModified?: DateTime },
>(
	state: S,
	adapter: EntityAdapter<Entity, EntityId>,
	payload: IAPIResult<Entity[]>,
	entityState: EntityState<Entity, EntityId>,
) => {
	if (payload.status !== 304) {
		if (state.lastModified !== undefined) {
			state.lastModified = Object.assign<DateTime, any>(DateTime.fromMillis(0), payload.lastModified)
		}
		if (payload.status === 500) {
			adapter.removeAll(entityState)
		} else {
			adapter.setAll(entityState, payload.result)
		}
	}
}

export type Middlewares<S> = ReadonlyArray<Middleware<object, S>>

export enum RenderType {
	EARLY,
	LATE,
}

export interface OrderEntitySelector<T, V, Id extends EntityId> {
	selectIds: (state: V) => Id[]
	selectEntities: (state: V) => Record<Id, T>
	selectAll: (state: V) => T[]
	selectTotal: (state: V) => number
	selectById: (state: V, id: Id) => T | undefined
}

export interface ILocalStorage {
	persist(state: Partial<DefaultRootState>): Promise<void>[]
	retrieve(): Promise<Partial<DefaultRootState>>

	getItem<T>(key: string): Promise<T | null>
	setItem<T>(key: string, value: T): Promise<T>
	removeItem(key: string): Promise<void>

	clear(): Promise<void>
}

const _internal_DO_NOT_USE_createStore: (
	rootReducer: Reducer<DefaultRootState> | ReducersMapObject<DefaultRootState>,
	middlewares: Middleware[],
) => EnhancedStore<
	DefaultRootState,
	UnknownAction,
	Tuple<
		[
			StoreEnhancer<{ dispatch: ThunkDispatch<DefaultRootState, Tuple<Middlewares<DefaultRootState>>, UnknownAction> }>,
			StoreEnhancer,
		]
	>
> = (
	rootReducer: Reducer<DefaultRootState> | ReducersMapObject<DefaultRootState>,
	middlewares: Middleware[],
): EnhancedStore<
	DefaultRootState,
	UnknownAction,
	Tuple<
		[
			StoreEnhancer<{ dispatch: ThunkDispatch<DefaultRootState, Tuple<Middlewares<DefaultRootState>>, UnknownAction> }>,
			StoreEnhancer,
		]
	>
> => {
	return configureStore({
		reducer: rootReducer,
		middleware: (
			getDefaultMiddleware, // Tuple<[StoreEnhancer<{   dispatch: ThunkDispatch<DefaultRootState, undefined, UnknownAction> }>>>
		) =>
			getDefaultMiddleware({
				serializableCheck: false,
			}).concat(middlewares),
		devTools: process.env.NODE_ENV === 'development',
	})
}

type StoreType = ReturnType<typeof _internal_DO_NOT_USE_createStore>

const createCoreStore = (
	rootReducer: Reducer<DefaultRootState> | ReducersMapObject<DefaultRootState>,
	middlewares: Middleware[],
	storage: ILocalStorage,
	items: string[],
	storageVersion: number,
	observers: ((store: StoreType) => Promise<any>)[],
	beforeObserver: (store: StoreType) => Promise<any>[],
	render: (store: StoreType) => void,
	renderType: RenderType,
): StoreType => {
	const stateRehydrateMiddleWare: Middleware = () => next => action => {
		const na = next(action)

		if (stateREHYDRATE.fulfilled.match(action)) {
			Promise.all(observers.map(observer => observer(store)))
				.then(() => {
					//setupPersister(store.dispatch, storage)
					if (renderType === RenderType.LATE) {
						// console.info('[coreStore:state]: Rendering component')
						render(store)
					}
				})
				.catch(() => {
					console.error('[coreStore:state]: unable to setup observers.')
				})
		}

		return na
	}

	const stateClearStorageMiddleWare: Middleware = () => next => action => {
		const na = next(action)

		if (stateCLEARSTORAGE.fulfilled.match(action)) {
			// console.log('clearing storage')
			// await storage.clear()
			storage.clear().catch(err => {
				console.error('[coreStore:state]: Error clearing storage', err)
			})
			// console.log('storage cleared')
		}

		return na
	}

	const store: StoreType = _internal_DO_NOT_USE_createStore(rootReducer, [
		stateRehydrateMiddleWare,
		stateClearStorageMiddleWare,
		...middlewares,
	])

	if (renderType === RenderType.EARLY) {
		// console.info('[coreStore:state]: Rendering component')
		render(store)
	}

	const storageImpl = () => {
		storage
			.retrieve()
			.then(res => {
				console.log(res)
				void Promise.all(beforeObserver(store)).then(() => {
					void store.dispatch(stateREHYDRATE(res)).catch(err => {
						console.error(err)
					})
				})
			})
			.catch(err => {
				console.error(err)
			})
	}

	storage
		.getItem<number>('_version')
		.then(res => {
			if (res !== null && res < storageVersion) {
				const promises: Promise<void>[] = []
				items.forEach(item => {
					promises.push(storage.removeItem(item))
				})
				Promise.all(promises)
					.then(() => {
						storage
							.setItem('_version', storageVersion)
							.then(() => {
								console.log(storageVersion)
								storageImpl()
							})
							.catch(() => {
								console.error('[coreStore:state]: unable to store version')
							})
					})
					.catch(() => {
						console.error('[coreStore:state]: unable to store version')
					})
			} else {
				storage
					.setItem('_version', storageVersion)
					.then(() => {
						storageImpl()
					})
					.catch(() => {
						console.error('[coreStore:state]: unable to store version')
					})
			}
		})
		.catch(() => {
			console.error('error getting version')
		})

	return store
}

type StateKeys = keyof DefaultRootState

export type { StoreType, StateKeys }

type IAppDispatch = StoreType['dispatch']

const useAppDispatch: () => ReturnType<typeof useDispatch<IAppDispatch>> = (): ReturnType<
	typeof useDispatch<IAppDispatch>
> => useDispatch<IAppDispatch>()
const useAppSelector: TypedUseSelectorHook<DefaultRootState> = useSelector

export { createCoreStore, type IAppDispatch, useAppDispatch, useAppSelector }
